import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Subscribe = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  
  // Check if this is an upgrade flow (user hit their limit)
  const isUpgrade = searchParams.get('upgrade') === 'true';
  const currentTier = user?.subscription_tier || 'free';

  // Already subscribed to Pro - show confirmation
  if (user?.is_subscribed && currentTier === 'pro') {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="pt-24 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <div className="w-16 h-16 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#d4af37]" />
              </div>
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                You're on Pro!
              </h1>
              <p className="text-slate-600 mb-6">
                You have access to 40 posts per month and all features.
              </p>
              <Button
                onClick={() => navigate('/create')}
                className="bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6"
              >
                Create a Post
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Already subscribed to Standard - show upgrade option
  if (user?.is_subscribed && currentTier === 'standard') {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <div className="pt-24 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
                You're subscribed!
              </h1>
              <p className="text-slate-600 mb-6">
                You have access to 20 posts per month.
              </p>
              
              {isUpgrade && (
                <div className="bg-[#0a2540] text-white rounded-xl p-5 mb-6 text-left">
                  <p className="font-semibold mb-2">Need more posts?</p>
                  <p className="text-sm text-slate-300 mb-4">
                    Upgrade to Pro for 40 posts per month — just £6 more.
                  </p>
                  <Button
                    onClick={() => handleSubscribe('pro')}
                    disabled={loading === 'pro'}
                    className="w-full bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028]"
                    data-testid="upgrade-to-pro-btn"
                  >
                    {loading === 'pro' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Upgrade to Pro — £15/month'
                    )}
                  </Button>
                </div>
              )}
              
              <Button
                onClick={() => navigate('/create')}
                variant={isUpgrade ? 'outline' : 'default'}
                className={isUpgrade ? '' : 'bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-12 px-6'}
              >
                {isUpgrade ? 'Maybe later' : 'Create a Post'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubscribe = async (tier) => {
    setLoading(tier);
    
    try {
      const response = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        origin_url: window.location.origin,
        tier: tier
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Navbar />
      
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0a2540] mb-4">
              Start getting enquiries
            </h1>
            <p className="text-slate-600">
              Turn your job photos into posts that make people message you.
            </p>
          </div>

          {/* Single Pricing Card */}
          <div className="bg-[#0a2540] rounded-3xl p-8 md:p-10 border-2 border-[#d4af37]">
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="text-5xl md:text-6xl font-bold text-white">£9</span>
              <span className="text-slate-400 text-lg">/month</span>
            </div>
            <p className="text-slate-400 text-center mb-8">Cancel anytime</p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Create engaging posts that generate enquiries</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Animated before/after GIFs</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Copy written to convert</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Post to Facebook & Instagram</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <span className="text-white">Download for any platform</span>
              </li>
            </ul>

            <Button
              onClick={() => handleSubscribe('standard')}
              disabled={loading === 'standard'}
              className="w-full bg-[#d4af37] text-[#0a2540] font-bold hover:bg-[#c5a028] h-14 text-lg"
              data-testid="subscribe-btn"
            >
              {loading === 'standard' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Get started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            
            <p className="text-slate-500 text-sm text-center mt-4">
              Secure payment via Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
